import api from "./api";
import logger from "../utils/logger";

const bookingService = {
  /**
   * Fetch estimate for a booking
   * @param {Object} estimateData - Data needed for estimate
   * @param {number} estimateData.categoryId - Service category ID
   * @param {Object} estimateData.details - Service-specific details
   * @returns {Promise<Object>} Estimate data with price range
   */
  fetchEstimate: async (estimateData) => {
    try {
      const response = await api.post("/bookings/estimate", estimateData);
      return response.data;
    } catch (error) {
      logger.error("Error fetching estimate", error);
      throw error;
    }
  },

  /**
   * Submit a new booking
   * @param {Object} bookingData - Complete booking data
   * @param {number} bookingData.categoryId - Service category ID
   * @param {Object} bookingData.details - Service details
   * @param {string} bookingData.requestedDateTime - Requested date/time
   * @param {Object} bookingData.serviceAddress - Service address
   * @param {Array} bookingData.estimatedPriceRange - Price range
   * @returns {Promise<Object>} Created booking
   */
  submitBooking: async (bookingData) => {
    try {
      const response = await api.post("/bookings", bookingData);
      return response.data;
    } catch (error) {
      logger.error("Error submitting booking", error);
      throw error;
    }
  },

  /**
   * Get all bookings for the authenticated user
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Bookings list with pagination
   */
  getBookings: async (params = {}) => {
    try {
      const response = await api.get("/bookings", { params });
      return response.data;
    } catch (error) {
      logger.error("Error fetching bookings", error);
      throw error;
    }
  },

  /**
   * Get a specific booking by ID
   * @param {number} bookingId - Booking ID
   * @returns {Promise<Object>} Booking details
   */
  getBookingById: async (bookingId) => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching booking", error);
      throw error;
    }
  },

  /**
   * Cancel a booking
   * @param {number} bookingId - Booking ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Reason for cancellation
   * @param {string} data.details - Additional details
   * @returns {Promise<Object>} Updated booking
   */
  cancelBooking: async (bookingId, data) => {
    try {
      const response = await api.put(`/bookings/${bookingId}/cancel`, data);
      return response.data;
    } catch (error) {
      logger.error("Error canceling booking", error);
      throw error;
    }
  },

  /**
   * Reschedule a booking
   * @param {number} bookingId - Booking ID
   * @param {Object} data - Reschedule data
   * @param {string} data.newRequestedDateTime - New date/time
   * @returns {Promise<Object>} Updated booking
   */
  rescheduleBooking: async (bookingId, data) => {
    try {
      const response = await api.put(`/bookings/${bookingId}/reschedule`, data);
      return response.data;
    } catch (error) {
      logger.error("Error rescheduling booking", error);
      throw error;
    }
  },

  /**
   * Submit a review for a booking
   * @param {number} bookingId - Booking ID
   * @param {Object} data - Review data
   * @param {number} data.rating - Rating (1-5)
   * @param {string} data.comment - Review comment
   * @returns {Promise<Object>} Updated booking with review
   */
  submitReview: async (bookingId, data) => {
    try {
      const response = await api.post("/reviews", {
        bookingId,
        ...data,
      });
      return response.data;
    } catch (error) {
      logger.error("Error submitting review", error);
      throw error;
    }
  },

  /**
   * Get reviews for a booking
   * @param {number} bookingId - Booking ID
   * @returns {Promise<Object>} Reviews data
   */
  getBookingReviews: async (bookingId) => {
    try {
      const response = await api.get(`/reviews/bookings/${bookingId}`);
      return response.data.data;
    } catch (error) {
      logger.error("Error fetching booking reviews", error);
      throw error;
    }
  },

  /**
   * Update payment method and retry payment
   * @param {number} bookingId - Booking ID
   * @param {Object} data - Payment data
   * @param {string} data.paymentMethodId - New payment method ID
   * @returns {Promise<Object>} Payment result
   */
  updatePaymentAndRetry: async (bookingId, data) => {
    try {
      const response = await api.post(
        `/bookings/${bookingId}/update-payment-and-retry`,
        data
      );
      return response.data;
    } catch (error) {
      logger.error("Error updating payment", error);
      throw error;
    }
  },
};

export default bookingService;
